import { msg } from "../locales/translate.ts";
import type { IPaymentSession } from "../types/types";

export type FailureAnalysisModel = "gpt-6.1-sol" | "gpt-6-astra";
export type FailureAnalysisLanguage = "en" | "ru";
type Request = (request: IAPIRequest) => Promise<IAPIResponse>;

export interface FailureAnalysisReport {
	title: string;
	summary: string;
	actions: { owner: string | null; action: string; expectedResult: string }[];
	evidence: { explanation: string; reference: string }[];
	unknowns: { question: string; neededData: string }[];
	technicalDetails: string;
	secondaryFindings: { finding: string; causalLinkProven: boolean }[];
}

function isFailureAnalysisReport(value: unknown): value is FailureAnalysisReport {
	if (!value || typeof value !== "object") return false;
	const data = value as Record<string, unknown>;
	const rows = (key: string, fields: string[], extra: (row: Record<string, unknown>) => boolean = () => true) => {
		const list = data[key];
		return Array.isArray(list) && list.every(item => {
			if (!item || typeof item !== "object") return false;
			return fields.every(field => typeof item[field] === "string") && extra(item);
		});
	};
	return typeof data.title === "string" && !!data.title.trim()
		&& typeof data.summary === "string" && !!data.summary.trim()
		&& typeof data.technicalDetails === "string"
		&& rows("actions", ["action", "expectedResult"], row => row.owner === null || typeof row.owner === "string")
		&& rows("evidence", ["explanation", "reference"])
		&& rows("unknowns", ["question", "neededData"])
		&& rows("secondaryFindings", ["finding"], row => typeof row.causalLinkProven === "boolean");
}

export async function collectFailureAnalysisData(transaction: IPaymentSession, request: Request, signal: AbortSignal, projectId: number, progress: (message: string) => void) {
	const read = async (method: string, data: object) => {
		signal.throwIfAborted();
		const response = await request({ method, data, signal, expectedProjectId: projectId, headers: { ignoreBusy: "true" } });
		signal.throwIfAborted();
		if (response.error) throw new Error(response.error.message || msg("additional3566"));
		if (response.data == null) throw new Error(msg("additional3567"));
		return response.data;
	};
	progress(msg("additional3568"));
	const channel = transaction.req?.merchant?.channels.find(item => item.linked?.id === transaction.channel_id);
	const hasProcessing = !!transaction.processing_id && channel?.integration?.common.type === "decta";
	const processing: IDectaSessionFull | null = hasProcessing ? await read(`processing/session/${transaction.processing_id}`, {}) : null;
	const log: unknown[] = [];
	if (processing) {
		if (!processing.session?.ssid) throw new Error(msg("additional3569"));
		const seen = new Set<string>();
		for (let page = 0; ; page++) {
			signal.throwIfAborted();
			if (page >= 1000) throw new Error(msg("additional3570"));
			progress(msg("templateLoadingProcessingLogsPageEntries_0a18ab74", { v0: page + 1, v1: log.length }));
			const result = await read("processing/log/get", { payment_id: processing.session.ssid, page, limit: 20 });
			const items = Array.isArray(result) ? result : result.items;
			if (!Array.isArray(items)) throw new Error(msg("additional3571"));
			const pagination = result.pagination;
			if (items.length) {
				const signature = JSON.stringify(items);
				if (seen.has(signature)) throw new Error(msg("additional3572"));
				seen.add(signature);
				log.push(...items);
			}
			if (pagination && Number.isInteger(pagination.totalPages) && pagination.totalPages >= 0) {
				if (page + 1 >= pagination.totalPages) {
					if (Number.isFinite(pagination.total) && log.length < pagination.total) throw new Error(msg("additional3573"));
					break;
				}
				if (!items.length) throw new Error(msg("additional3574"));
			} else if (!items.length) break;
		}
	} else {
		progress(msg("additional3575"));
		const entries = await read("session/log", { id: transaction.id });
		if (!Array.isArray(entries)) throw new Error(msg("additional3576"));
		log.push(...entries);
	}
	return { transaction, processing, log };
}

export async function requestFailureAnalysis(transactionData: Awaited<ReturnType<typeof collectFailureAnalysisData>>, model: FailureAnalysisModel, userPrompt: string, signal: AbortSignal, authorization: string, lang: FailureAnalysisLanguage = "en") {
	signal.throwIfAborted();
	const controller = new AbortController();
	const abort = () => controller.abort(signal.reason);
	signal.addEventListener("abort", abort, { once: true });
	const timeoutError = new Error(msg("additional3577"));
	const timeout = setTimeout(() => controller.abort(timeoutError), 20 * 60 * 1000);
	try {
		return await pollFailureAnalysis(transactionData, model, userPrompt, controller.signal, authorization, lang, timeoutError);
	} finally {
		clearTimeout(timeout);
		signal.removeEventListener("abort", abort);
	}
}

async function pollFailureAnalysis(transactionData: Awaited<ReturnType<typeof collectFailureAnalysisData>>, model: FailureAnalysisModel, userPrompt: string, signal: AbortSignal, authorization: string, lang: FailureAnalysisLanguage, timeoutError: Error) {
	const startedAt = Date.now();
	const post = async (path: string, body: object) => {
		signal.throwIfAborted();
		if (Date.now() - startedAt >= 20 * 60 * 1000) throw timeoutError;
		const response = await fetch(`https://valera.impaya.com/api/v1/analytics/transaction${path}`, {
			method: "POST", signal,
			headers: { Authorization: authorization, "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		if (!response.ok) throw new Error(msg("templateAnalysisRequestFailedHttp_ca2aa19c", { v0: response.status }));
		const result = await response.json();
		signal.throwIfAborted();
		if (Date.now() - startedAt >= 20 * 60 * 1000) throw timeoutError;
		if (!result || result.error || result.httpStatus >= 400) throw new Error(msg("theAnalysisServiceCouldNotCompleteTheRequest"));
		return result.data;
	};
	const task = await post("", { transactionData, model, language: lang === "en" ? "eng" : lang, ...(userPrompt.trim() ? { userPrompt: userPrompt.trim() } : {}) });
	if (typeof task?.uid !== "string" || !task.uid.trim()) throw new Error(msg("theAnalysisServiceReturnedNoTaskId"));
	for (let attempt = 0; ; attempt++) {
		await waitForAnalysisPoll(signal, Math.max(10000, 40000 - attempt * 10000));
		const data = await post("/result", { uid: task.uid });
		if (data?.status === "processing") continue;
		if (isFailureAnalysisReport(data)) {
			return { report: data, language: lang, responseTime: Date.now() - startedAt };
		}
		if (typeof data?.problem !== "string" || !data.problem.trim() || typeof data?.solution !== "string" || !data.solution.trim()) {
			throw new Error(msg("theAnalysisServiceReturnedAnInvalidReportNoProblemOrSolution"));
		}
		const titles = lang === "ru" ? ["Проблема", "Решение"] : ["Problem", "Solution"];
		return { text: `## ${titles[0]}\n${data.problem}\n\n## ${titles[1]}\n${data.solution}`, language: lang, responseTime: Date.now() - startedAt };
	}
}

function waitForAnalysisPoll(signal: AbortSignal, delay: number): Promise<void> {
	signal.throwIfAborted();
	return new Promise((resolve, reject) => {
		const abort = () => {
			clearTimeout(timer);
			reject(signal.reason);
		};
		const timer = setTimeout(() => {
			signal.removeEventListener("abort", abort);
			resolve();
		}, delay);
		signal.addEventListener("abort", abort, { once: true });
	});
}
